import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface IStrand {
  id: number;
  displayName: string;
  subject: {
    id: number;
    displayName: string;
  };
  curriculum: {
    id: number;
    displayName: string;
  };
  grade: number;
  gradeDisplay: string;
  order: number;
  subStrandsCount: number;
  created: string;
  modified: string;
}

export interface IStrandFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  subjectId?: number;
  curriculumId?: number;
  grade?: number;
}

export interface IStrandApiResponse {
  data: IStrand[];
  page: number;
  pages: number;
  perPage: number;
  total: number;
}

export interface IStrandDetailResponse {
  id: number;
  displayName: string;
  subject: {
    id: number;
    displayName: string;
  };
  curriculum: {
    id: number;
    displayName: string;
  };
  grade: number;
  gradeDisplay: string;
  order: number;
  subStrandsCount: number;
  created: string;
  modified: string;
}

export const StrandsApi = createApi({
  reducerPath: 'StrandsApi',
  baseQuery,
  tagTypes: ['Strand'],
  endpoints: builder => ({
    getStrands: builder.query<IStrandApiResponse, IStrandFilters>({
      query: filters => {
        const queryParams = new URLSearchParams();
        if (filters.page) queryParams.append('page', filters.page.toString());
        if (filters.pageSize)
          queryParams.append('pageSize', filters.pageSize.toString());
        if (filters.search) queryParams.append('search', filters.search);
        if (filters.subjectId)
          queryParams.append('subjectId', filters.subjectId.toString());
        if (filters.curriculumId)
          queryParams.append('curriculumId', filters.curriculumId.toString());
        if (filters.grade)
          queryParams.append('grade', filters.grade.toString());

        return `/academics/strands?${queryParams.toString()}`;
      },
      providesTags: ['Strand'],
    }),

    getStrandById: builder.query<IStrandDetailResponse, number>({
      query: id => `/academics/strands/${id}`,
      providesTags: ['Strand'],
    }),

    createStrand: builder.mutation<IStrandDetailResponse, Partial<IStrand>>({
      query: strandData => ({
        url: '/academics/strands',
        method: 'POST',
        body: strandData,
      }),
      invalidatesTags: ['Strand'],
    }),

    updateStrand: builder.mutation<
      IStrandDetailResponse,
      { id: number; data: Partial<IStrand> }
    >({
      query: ({ id, data }) => ({
        url: `/academics/strands/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Strand'],
    }),

    deleteStrand: builder.mutation<void, number>({
      query: id => ({
        url: `/academics/strands/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Strand'],
    }),
  }),
});

export const {
  useGetStrandsQuery,
  useGetStrandByIdQuery,
  useCreateStrandMutation,
  useUpdateStrandMutation,
  useDeleteStrandMutation,
} = StrandsApi;
